import React, { useEffect, useState } from 'react';

interface ConnectorOverlayProps {
  sourceId: string;
  targetId: string;
}

const ConnectorOverlay: React.FC<ConnectorOverlayProps> = ({ sourceId, targetId }) => {
  const [coords, setCoords] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);

  useEffect(() => {
    const updateCoords = () => {
      const sourceEl = document.getElementById(sourceId);
      const targetEl = document.querySelector(`[id="${targetId}"]`);

      if (sourceEl && targetEl) {
        const sourceRect = sourceEl.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();

        setCoords({
          x1: sourceRect.right,
          y1: sourceRect.top + sourceRect.height / 2,
          x2: targetRect.left,
          y2: targetRect.top + targetRect.height / 2,
        });
      } else {
        setCoords(null);
      }
    };

    updateCoords();

    const handleUpdate = () => {
      requestAnimationFrame(updateCoords);
    };

    window.addEventListener('resize', handleUpdate);

    const pdfContainer = document.querySelector('.PdfHighlighter');
    if (pdfContainer) {
      pdfContainer.addEventListener('scroll', handleUpdate);
    }

    const interval = setInterval(handleUpdate, 200);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      if (pdfContainer) {
        pdfContainer.removeEventListener('scroll', handleUpdate);
      }
      clearInterval(interval);
    };
  }, [sourceId, targetId]);

  if (!coords) return null;

  const shortHorizontalDistance = Math.min(120, (coords.x2 - coords.x1) * 0.4);
  const bendX = coords.x1 + shortHorizontalDistance;

  const lastSegmentLength = Math.sqrt(
    Math.pow(coords.x2 - bendX, 2) + Math.pow(coords.y2 - coords.y1, 2)
  );

  let adjustedX2 = coords.x2;
  let adjustedY2 = coords.y2;

  if (lastSegmentLength > 10) {
    const ratio = 10 / lastSegmentLength;
    adjustedX2 = coords.x2 - ratio * (coords.x2 - bendX);
    adjustedY2 = coords.y2 - ratio * (coords.y2 - coords.y1);
  }

  return (
    <svg
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    >
      <path
        d={`
          M ${coords.x1},${coords.y1}
          L ${bendX},${coords.y1}
          L ${adjustedX2},${adjustedY2}
        `}
        fill="none"
        stroke="#3E8AFF"
        strokeWidth={1.5}
        markerEnd="url(#arrowhead)"
      />
      <circle cx={coords.x1} cy={coords.y1} r={4} fill="#3E8AFF" />
      <circle cx={adjustedX2} cy={adjustedY2} r={4} fill="#3E8AFF" />
    </svg>
  );
};

export default ConnectorOverlay;
